'use client'
import { useRef } from 'react'
import type { AudioState } from './Scene3D'
import Scene3D from './Scene3D'
import TrustedBy from './TrustedBy'
import LogosMarquee from './LogosMarquee'
import Icon from './Icon'
import ScrollIndicator from './ScrollIndicator'
import { useLang } from '../context/LangContext'
import { t, hero as heroT } from '../lib/translations'

export default function Hero({ audioStateRef }: { audioStateRef: React.MutableRefObject<AudioState> }) {
  const heroRef = useRef<HTMLElement | null>(null)
  const lang = useLang()

  return (
    <section ref={heroRef} className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden">
      <Scene3D heroRef={heroRef} audioStateRef={audioStateRef} />

      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#07070d] pointer-events-none" />

      <ScrollIndicator />

      <div className="w-full">
        <div className="relative z-10 max-w-5xl mx-auto px-8 sm:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight lg:leading-[1.3] flex flex-col">
            <span className="gradient-text">{t(lang, heroT.title1)}</span>
            <span className="text-white">{t(lang, heroT.title2)}</span>
          </h1>

          <p className="mt-6 text-lg sm:text-xl text-[#94a3b8] max-w-2xl mx-auto leading-relaxed">
            {t(lang, heroT.description)}
          </p>

          <div className="animate-fade-in-up animation-delay-400 mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href={t(lang, heroT.ctaPrimaryUrl)}
              className="w-full sm:w-auto text-center group relative px-8 py-3.5 rounded-xl bg-gradient-to-r from-[var(--brand-600)] to-[var(--brand-500)] font-semibold text-sm hover:from-[var(--brand-500)] hover:to-[var(--brand-300)] transition-all duration-200 shadow-2xl shadow-[rgb(var(--brand-rgb)/0.25)] glow btn-lightning"
            >
              <span className="relative z-10">{t(lang, heroT.ctaPrimary)}</span>
            </a>
            <a
              href="#cta"
              className="w-full sm:w-auto text-center group px-8 py-3.5 rounded-xl bg-white/[0.08] border border-white/[0.2] font-medium text-sm text-white hover:bg-white/[0.15] hover:border-white/[0.4] transition-all duration-300"
              onClick={(e) => {
                e.preventDefault()
                document.getElementById('cta')?.scrollIntoView({ behavior: 'smooth' })
              }}
            >
              <span className="flex justify-center items-center gap-2">
                {t(lang, heroT.ctaSecondary)}
                <Icon name="arrow-right" className="w-4 h-4 group-hover:translate-x-1 rtl:rotate-180 rtl:group-hover:translate-x-[-0.25rem] transition-transform" />
              </span>
            </a>
          </div>
        </div>
      </div>

      <div className="relative z-10 w-full max-w-5xl mx-auto px-8 sm:px-8 text-center mt-16">
        <TrustedBy />
        <LogosMarquee marginTop="mt-8" />
      </div>
    </section>
  )
}
